// Everything Clevis does, in one list: the source for plan checkboxes in the
// master admin, the "what's included" lists companies see when choosing a
// plan, and the switches that turn a module on or off per plan.
//
// Tiers stack: Apprentice ⊂ Journeyman ⊂ Master. `key` marks a switch the app
// enforces (menu, pages, and for the ones that cost money to run, the
// server). Items without a key are included in every plan; their tier only
// decides where the pricing card first mentions them.

export const PLAN_TIERS = ["apprentice", "journeyman", "master"] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

export const PLAN_TIER_DEFAULTS: Record<PlanTier, { name: string; seats: number | null; pitch: string }> = {
  apprentice: { name: "Apprentice", seats: 15, pitch: "Everything to run the work and get paid." },
  journeyman: { name: "Journeyman", seats: 30, pitch: "For growing companies with several crews and an office." },
  master: { name: "Master", seats: null, pitch: "Everything Clevis does, for multi-department and builder work." },
};

export interface PlanFeatureDef {
  /** The switch name (absent = always included). */
  key?: string;
  label: string;
  group: string;
  tier: PlanTier;
  /** The server refuses it too (features that cost money to run). */
  server?: boolean;
  soon?: boolean;
}

export const PLAN_FEATURE_GROUPS = [
  "Run the work",
  "Get paid",
  "Customers and texting",
  "Team and time",
  "Money and accounting",
  "Inventory and purchasing",
  "Builders and production",
  "Setup and support",
] as const;

export const PLAN_FEATURES: PlanFeatureDef[] = [
  // Run the work
  { label: "Jobs, scheduling and a dispatch board (by tech, timeline, department)", group: "Run the work", tier: "apprentice" },
  { label: "Field app for iPhone and Android: My Day, job details, photos, GPS, works offline", group: "Run the work", tier: "apprentice" },
  { label: "Job types, visits and warranty claims under one job", group: "Run the work", tier: "apprentice" },
  { label: "Job timeline that shows who did what", group: "Run the work", tier: "apprentice" },
  { label: "Found & done: what the tech found, work and parts logged on the job", group: "Run the work", tier: "apprentice" },
  { label: "Customer sign-off when the job is finished", group: "Run the work", tier: "apprentice" },
  { label: "Pipeline board: partly done, waiting on parts, not billed, unpaid", group: "Run the work", tier: "journeyman" },
  { key: "routing", label: "Live tech map and routes that re-plan themselves", group: "Run the work", tier: "journeyman" },
  { label: "Available-tech dispatch: offer a job to whoever is free nearby", group: "Run the work", tier: "journeyman" },
  { key: "jobFlows", label: "Job flows: choose what techs see on each job type", group: "Run the work", tier: "journeyman" },

  // Get paid
  { label: "Estimates and invoices with e-signature, PDFs and email", group: "Get paid", tier: "apprentice" },
  { label: "Pricebook with kits and bundles (hide the parts' prices)", group: "Get paid", tier: "apprentice" },
  { label: "Card payments into your own Stripe account, on site or by texted link", group: "Get paid", tier: "apprentice" },
  { label: "Techs quote from the pricebook and collect on site (costs stay hidden)", group: "Get paid", tier: "apprentice" },
  { key: "jobProfit", label: "Job profit: revenue against labor, parts and expenses", group: "Get paid", tier: "journeyman" },
  { key: "serviceAgreements", label: "Maintenance agreements: billed monthly to yearly on their own, visits opened as jobs", group: "Get paid", tier: "journeyman", server: true },

  // Customers and texting
  { label: "Customer records with full history, search and CSV export", group: "Customers and texting", tier: "apprentice" },
  { key: "requests", label: "Website quote form: embed it or post your own, lead source tracking", group: "Customers and texting", tier: "apprentice" },
  { label: "Texts: \"on my way\" and \"who's coming\" with the tech's photo and bio", group: "Customers and texting", tier: "apprentice" },
  { label: "Job-completion emails to the customer", group: "Customers and texting", tier: "apprentice" },
  { label: "Company documents (W-9, insurance certificates) sent and logged", group: "Customers and texting", tier: "apprentice" },
  { key: "autoTexts", label: "Automatic texts: who's coming on assignment, review requests with your Google link", group: "Customers and texting", tier: "journeyman", server: true },
  { key: "leadNumbers", label: "Clevis Ring: a phone number per ad or referrer, your main line forwarded in, the caller popped on every office screen, every call logged by source (carrier charges passed through at cost)", group: "Customers and texting", tier: "journeyman", server: true },
  { key: "aiChat", label: "AI chat on your website that books visits for the office to approve", group: "Customers and texting", tier: "journeyman", server: true },
  { key: "emailMarketing", label: "Mailchimp: new customers of the types you choose join your audience on their own", group: "Customers and texting", tier: "journeyman", server: true },
  { key: "referrals", label: "Clevis Relay: referral rewards — every customer gets a link, gift cards go out on their own when the friend's job is paid", group: "Customers and texting", tier: "master", server: true },
  { key: "templates", label: "Agreements and templates signed on site", group: "Customers and texting", tier: "journeyman" },
  { key: "customerPortal", label: "Customer portal: invoices and payments, estimates to sign, W-9 and certificates, job status, send in POs", group: "Customers and texting", tier: "journeyman", server: true },

  // Team and time
  { key: "workforce", label: "Time clock with GPS and timesheets", group: "Team and time", tier: "apprentice" },
  { label: "Field tech role: techs see only their own jobs", group: "Team and time", tier: "apprentice" },
  { label: "Dashboard and menu each person arranges, with favorites", group: "Team and time", tier: "apprentice" },
  { key: "pieceRate", label: "Hourly and piece-rate pay with a weekly pay report", group: "Team and time", tier: "journeyman" },
  { key: "departments", label: "Departments: route jobs and requests to teams, filter by team", group: "Team and time", tier: "journeyman" },
  { key: "customAccess", label: "Custom roles and per-person access, enforced on the server", group: "Team and time", tier: "journeyman" },
  { key: "shiftMarketplace", label: "Shift marketplace and \"near me\" for open work", group: "Team and time", tier: "master" },
  { key: "payroll", label: "Payroll runs with overtime, exports for ADP, Paychex, Gusto and more", group: "Team and time", tier: "master", server: true },

  // Money and accounting
  { key: "accountingSync", label: "QuickBooks Online and Desktop: customers, invoices, payments, estimates", group: "Money and accounting", tier: "apprentice" },
  { label: "Learns your QuickBooks setup from your own invoices", group: "Money and accounting", tier: "apprentice" },
  { key: "reporting", label: "Reports and CSV exports", group: "Money and accounting", tier: "apprentice" },
  { key: "expenses", label: "Expenses to QuickBooks bills, techs snap receipts (AI reads them)", group: "Money and accounting", tier: "journeyman", server: true },
  { key: "aiInsights", label: "Ask Clevis: questions about your business, answered from your data", group: "Money and accounting", tier: "master", server: true },

  // Inventory and purchasing
  { key: "inventory", label: "Inventory: stock, bins, reorder levels, barcode scanning", group: "Inventory and purchasing", tier: "journeyman" },
  { label: "Parts ordered per job, waiting-on-parts tracking", group: "Inventory and purchasing", tier: "journeyman" },
  { key: "invoiceToInventory", label: "AI receiving: a supplier invoice puts parts into stock", group: "Inventory and purchasing", tier: "journeyman", server: true },
  { key: "purchasing", label: "Purchase orders: emailed to suppliers as PDFs, AI reads the order confirmation, receive into stock", group: "Inventory and purchasing", tier: "journeyman", server: true },

  // Builders and production
  { key: "projects", label: "Projects: builders, developments, lots and jobs, with a project calendar", group: "Builders and production", tier: "master" },
  { label: "Bid options (\"Elevation A\") carried from bid to invoice", group: "Builders and production", tier: "master" },
  { key: "emailIntake", label: "Work orders and email intake: AI reads builder POs, starts and completions", group: "Builders and production", tier: "master", server: true },
  { label: "Builder cleanup for messy imported customers", group: "Builders and production", tier: "master" },

  // Setup and support
  { label: "Industry presets and your own words for jobs and techs", group: "Setup and support", tier: "apprentice" },
  { label: "Move from FieldPulse: customers, jobs, items, estimates, invoices", group: "Setup and support", tier: "apprentice" },
  { label: "In-app support and remote help", group: "Setup and support", tier: "apprentice" },
  { label: "Multiple locations", group: "Setup and support", tier: "master" },
  { label: "Priority support and hands-on onboarding", group: "Setup and support", tier: "master" },
];

const RANK: Record<PlanTier, number> = { apprentice: 0, journeyman: 1, master: 2 };

/** The switches for a tier: everything at or below it on, the rest off. */
export function tierFeatureFlags(tier: PlanTier): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const f of PLAN_FEATURES) if (f.key) out[f.key] = RANK[f.tier] <= RANK[tier];
  return out;
}

/** The catalog items a plan includes, given its switches (always-included items by tier). */
export function planIncludes(features: Record<string, boolean>, tier: PlanTier | null): PlanFeatureDef[] {
  return PLAN_FEATURES.filter((f) => (f.key ? features[f.key] === true : tier === null || RANK[f.tier] <= RANK[tier]));
}

/** Best guess of a plan's tier from its switches (for "everything in X, plus"). */
export function tierOf(features: Record<string, boolean>): PlanTier {
  let best: PlanTier = "apprentice";
  for (const t of PLAN_TIERS) {
    const want = tierFeatureFlags(t);
    if (Object.entries(want).every(([k, on]) => !on || features[k] === true)) best = t;
  }
  return best;
}
